import { useQuery } from "@apollo/client/react";
import { type NetworkStatus } from "@apollo/client";
import type { DocumentNode } from "graphql";

/**
 * Canonical Apollo options for every entity LIST query in the CRM.
 *
 * `fetchPolicy: "cache-and-network"` is the load-bearing field: it serves any
 * cached connection immediately AND fires a network request in parallel, so a
 * list can never get stuck rendering a stale/empty normalized cache entry. The
 * default (`cache-first`) does NOT refetch when a cached `*Collection` exists,
 * which produced the "table empty until manual refresh" bug on the time-entries
 * `pending-approval` view (the page mounted with a filter, hit a cached empty
 * connection, and never went to the network until `refetch()` forced it).
 *
 * Exported so the build-time guard (`scripts/validate-paginated-collection-hooks.mjs`)
 * and the unit test can assert every list hook routes through this helper.
 */
export const PAGINATED_COLLECTION_QUERY_OPTIONS = {
  errorPolicy: "all",
  notifyOnNetworkStatusChange: true,
  fetchPolicy: "cache-and-network",
} as const;

/** Relay `pageInfo` shape — codegen `PageInfo` is structurally assignable. */
export interface PageInfoLike {
  endCursor?: string | null;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  startCursor?: string | null;
}

/**
 * Minimal structural shape of a Relay-style pg_graphql connection. Codegen
 * connection types (`time_entriesCollection`, `contactsCollection`, …) are all
 * structurally assignable to this.
 */
export interface RelayConnection<TNode> {
  edges?: ReadonlyArray<{ node?: TNode | null } | null> | null;
  totalCount?: number | null;
  pageInfo?: PageInfoLike | null;
}

/**
 * Normalized result shape consumed by the UniversalEntityPage data layer.
 * `pageInfo` is coalesced to `undefined` (never `null`) so it stays assignable
 * to the `EntityHooks` `PaginatedResult` contract.
 */
export interface PaginatedCollectionResult<TMapped> {
  data:
    | {
        data: TMapped[];
        totalCount: number;
        pageInfo?: PageInfoLike;
      }
    | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  networkStatus: NetworkStatus;
}

/**
 * Shared implementation of every entity `usePaginated` list hook.
 *
 * Centralizes the three things each hand-rolled wrapper used to duplicate (and
 * which drifted apart): the canonical Apollo options, the
 * edges → node → filter-null → map pipeline, and the normalized return shape.
 * Per-entity hooks supply only what genuinely differs: the query document, the
 * collection selector, and the node mapper.
 *
 * @param document          codegen query document (e.g. `GetTimeEntriessDocument`)
 * @param params            GraphQL variables (filter / pagination)
 * @param selectConnection  picks the `*Collection` field off the query result
 * @param mapNode           maps a raw node to the entity shape (e.g. attach nodeId)
 */
export function usePaginatedCollection<TQuery, TNode, TMapped>(
  document: DocumentNode,
  params: Record<string, unknown> | undefined,
  selectConnection: (data: TQuery) => RelayConnection<TNode> | null | undefined,
  mapNode: (node: TNode) => TMapped
): PaginatedCollectionResult<TMapped> {
  const result = useQuery<TQuery>(document, {
    ...PAGINATED_COLLECTION_QUERY_OPTIONS,
    variables: params,
  });

  const connection = result.data ? selectConnection(result.data) : undefined;

  return {
    data: result.data
      ? {
          data:
            connection?.edges
              ?.map((edge) => edge?.node)
              .filter((node): node is NonNullable<TNode> => node != null)
              .map((node) => mapNode(node)) ?? [],
          totalCount: connection?.totalCount ?? 0,
          pageInfo: connection?.pageInfo ?? undefined,
        }
      : undefined,
    isLoading: result.loading,
    error: result.error ?? null,
    refetch: result.refetch,
    networkStatus: result.networkStatus,
  };
}
